import { useNotify, UserPermission } from '@globaleris/ui.library'
import { Box, FormControlLabel, Switch, Typography } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity } from '../../api'
import { ProfileModel } from '../../api/Argentia/endpoints/profiles/model/profiles'
import { EnumTipoPerfil } from '../../api/oData/enums/enumTipoPerfil'
import { EnumSubtipoPerfil } from '../../api/oData/enums/enumSubtipoPerfil'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { enumToMapValues, enumValueToKey, MapToArray } from '../../helpers'
import { hasPermission } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { Resources } from '../../model/RBAC/constants'
import {
  getActiveCommodities,
  getActiveCommodity,
  getActiveCommodityFilter,
  storeActiveCommodity,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'
import { ProfilesDataTable } from './components/profilesDataTable'
import { EnumProfilesTabs } from './profiles.model'
import { fetchProfilesByType } from './profiles.services'

interface ProfilesPageProps {
  location?: {
    state: {
      pageProp?: string
    }
  }
}

export const ProfilesPage: React.FC<ProfilesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'profiles'])
  const history = useHistory()
  const commodities = getActiveCommodities()
  const activeCommodityFilter = getActiveCommodityFilter()
  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (activeCommodityFilter
      ? activeCommodityFilter
      : commodities !== undefined && commodities?.length > 0
        ? enumValueToKey(EnumCommodity, commodities[0])
        : EnumCommodity.GasNatural)
  const { activeTab } = usePageState()
  const { error } = useNotify()

  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)

  const [profilesData, setProfilesData] = useState<ProfileModel[]>([])
  const [showHistoric, setShowHistoric] = useState<boolean>(false)

  const [isFetching, setIsFetching] = useState<boolean>(false)

  // #region Handlers Starts

  // ------------------- Commodity Chips Buttons Handlers -----------------------------//
  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity(commodity)
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
  }

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleSave = async () => {
    // Save Logic
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const getProfilesData = useCallback(async (commodity: number, tabIdx: number) => {
    setIsFetching(true)
    await fetchProfilesByType(commodity, tabIdx)
      .then(response => {
        if (response.success && response.data) {
          setProfilesData(response.data)
        } else {
          error(t('profiles:fetchErrors.profilesData'))
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [])

  useEffect(() => {
    getProfilesData(commodity, filteredTabsArray[currentTab]?.id!)
  }, [commodity, currentTab])

  // #endregion Hooks Ends

  // #region Render Functions Starts
  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!isFetching}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  // #endregion Render Functions Ends

  // #region Tabs Starts
  const handleTabPermission = (tabId: EnumTipoPerfil) => {
    switch (tabId) {
      case EnumTipoPerfil.Facturacion:
        return hasPermission(Resources.InvoicesPage, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      case EnumTipoPerfil.LeadEstandar:
        return hasPermission(Resources.Leads, [UserPermission.EDIT, UserPermission.READ_ONLY])
      default:
        return false
    }
  }

  const tabsArray = MapToArray(enumToMapValues(EnumProfilesTabs))
  const tabsDefinition: Map<number, Tab> = new Map(
    tabsArray.map((tab, tabIndex) => [
      tabIndex,
      {
        id: tab.value,
        label: t(`profiles:tabs.${tab.name}`),
        hasPermission: handleTabPermission(tab.value)
      }
    ])
  )

  const filteredTabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)
    }
  }

  const renderTabContent = (activeTab: number) => {
    const filteredProfiles = showHistoric
      ? profilesData
      : profilesData.filter(p => p.profileSubtype !== EnumSubtipoPerfil.Historico)

    return (
      filteredTabsArray[activeTab]?.id && (
        <>
          <Box display="flex" alignItems="center" justifyContent="flex-end" mb={1} mr={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showHistoric}
                  onChange={e => setShowHistoric(e.target.checked)}
                  color="primary"
                  disabled={isFetching}
                />
              }
              label={
                <Typography variant="subtitle2" color="secondary">
                  {t('profiles:filters.showHistoric')}
                </Typography>
              }
              disabled={isFetching}
            />
          </Box>
          <ProfilesDataTable
            commodity={commodity}
            profileType={filteredTabsArray[activeTab]?.id}
            profilesData={filteredProfiles}
            isFetching={isFetching}
            onProfileDeleted={() => getProfilesData(commodity, filteredTabsArray[currentTab]?.id!)}
          />
        </>
      )
    )
  }

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])
  // #endregion Tab Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('profiles:header.title')}>
        <Box display="flex" justifyContent="flex-end">
          {renderCommodities()}
        </Box>
      </PageHeader>

      <TabsWithPermissions
        tabs={tabsDefinition}
        tabDefault={currentTab}
        onTabChange={onTabChangeHandler}
      />

      {renderTabContent(activeTab ? activeTab : currentTab)}
    </Page>
  )
}
